import {useCallback, useEffect, useRef, useState} from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const readStoredWidth = (storageKey, fallback) => {
    if (typeof window === 'undefined' || !storageKey) return fallback;
    try {
        const raw = window.localStorage.getItem(storageKey);
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    } catch {
        return fallback;
    }
};

// Headless drag-resize for a panel whose width is laid out in pixels.
// Returns a width state, a setter, and a `startDrag` handler to attach to
// the resize handle's onMouseDown / onTouchStart / onPointerDown. The
// handler captures the current width as the drag baseline, listens to
// global move/up events while active, and clamps the result into [min,
// max]. Persists to localStorage if storageKey is supplied.
export const useResizablePanel = ({
    storageKey = '',
    defaultWidth = 360,
    min = 240,
    max = 720,
} = {}) => {
    const [width, setWidth] = useState(() =>
        clamp(readStoredWidth(storageKey, defaultWidth), min, max),
    );
    const widthRef = useRef(width);
    widthRef.current = width;

    useEffect(() => {
        if (typeof window === 'undefined' || !storageKey) return;
        try {
            window.localStorage.setItem(storageKey, String(width));
        } catch {
            // ignore quota / privacy-mode errors
        }
    }, [storageKey, width]);

    const startDrag = useCallback(
        (event) => {
            if (!event) return;
            event.preventDefault();
            const startX =
                event.clientX ??
                event.touches?.[0]?.clientX ??
                event.changedTouches?.[0]?.clientX ??
                0;
            const startWidth = widthRef.current;

            const previousCursor = document.body.style.cursor;
            const previousUserSelect = document.body.style.userSelect;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            const onMove = (moveEvent) => {
                const currentX =
                    moveEvent.clientX ??
                    moveEvent.touches?.[0]?.clientX ??
                    moveEvent.changedTouches?.[0]?.clientX;
                if (typeof currentX !== 'number') return;
                const next = clamp(startWidth + (currentX - startX), min, max);
                setWidth(next);
            };
            const onEnd = () => {
                document.body.style.cursor = previousCursor;
                document.body.style.userSelect = previousUserSelect;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onEnd);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend', onEnd);
                window.removeEventListener('touchcancel', onEnd);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onEnd);
            window.addEventListener('touchmove', onMove, {passive: false});
            window.addEventListener('touchend', onEnd);
            window.addEventListener('touchcancel', onEnd);
        },
        [min, max],
    );

    return {width, setWidth, startDrag, min, max};
};
